class StayAISubscriptions extends HTMLElement {
  // Stay AI app ID for identifying Stay AI selling plan groups
  static STAY_APP_ID = "5859381";

  constructor() {
    super();
    this.productJSON = this.getAttribute("productData");
    this.product = JSON.parse(this.productJSON);
    this.initialVariant = this.getAttribute("currentVariantId");
    this.sellingPlanGroups = JSON.parse(this.getAttribute("sellingPlanGroups"));
    this.productFormId = this.getAttribute("productFormId");
    this.frequencyVisible = this.getAttribute("frequencyVisible");

    // Integration hook: Flag to bypass updateProductPrice() when external pricing (e.g., Kaching) is active
    this._useExternalPricing = false;
  }
  connectedCallback() {
    this.initWidget();
    this.setupEventListeners();
  }

  initWidget() {
    this.purchaseTypeSubscription = this.querySelector("[for='purchaseTypeSubscription']");
    this.purchaseTypeOneTime = this.querySelector("[for='purchaseTypeOneTime']");
    this.sellingPlanSelector = this.querySelector(".stayai-subscription-dropdown");
    this.subscriptionBox = this.querySelector("[data-retextion-subscription-box]");
    this.subscriptionBoxContainer = this.querySelector(".stayai-subscription-frequency-container");
    this.radioOneTime = this.querySelector('input[value="purchaseTypeOneTime"]');
    this.radioSubscription = this.querySelector('input[value="purchaseTypeSubscription"]');

    if (!this.product) {
      console.error(">>> [StayAI] initWidget: productData missing");
      return;
    }
    console.log(">>> [StayAI] this.product:", this.product);
    const variants = this.product?.variants ?? [];
    if (!variants.length) {
      console.error(">>> [StayAI] initWidget: no variants");
      return;
    }

    if (
      !this.sellingPlanSelector ||
      !this.subscriptionBox ||
      !this.purchaseTypeSubscription ||
      !this.purchaseTypeOneTime
    ) {
      console.error(">>> [StayAI] initWidget: required DOM missing");
      return;
    }

    const selectedInitialVariant = variants.find((variant) => variant.id === Number(this.initialVariant));
    this.currentVariant = selectedInitialVariant ?? variants[0];
    this.updateSellingPlans();
  }
  // Set up event listeners for the purchase type, frequency and variant selectors
  setupEventListeners() {
    const purchaseTypeRadioButtons = this.querySelectorAll('input[name="purchaseType"]');
    if (!purchaseTypeRadioButtons.length) {
      console.error(">>> [StayAI] purchaseType inputs missing");
    }
    // Handle purchase type change
    purchaseTypeRadioButtons.forEach((radio) =>
      radio.addEventListener("change", () => {
        this[radio.value === "purchaseTypeSubscription" ? "showSubscriptionBox" : "hideSubscriptionBox"]();
        if (this.sellingPlanSelector) this.updateProductPrice(this.sellingPlanSelector.value);
      }),
    );
    // Handle frequency selector change
    if (!this.sellingPlanSelector) {
      console.error(">>> [StayAI] setupEventListeners: sellingPlanSelector missing");
    } else {
      this.sellingPlanSelector.addEventListener("change", (e) => {
        this.updateSellingPlanInput(e.target.value);
        this.updateProductPrice(e.target.value);
      });
    }
    // Handle variant change from URL
    window.addEventListener("popstate", this.updateVariantID());
    this.observeUrlChanges();
    // Handle browser back/forward navigation
    window.addEventListener("pageshow", (event) => {
      if (event.persisted || performance.getEntriesByType("navigation")[0]?.type === "back_forward") {
        const label = this.isSubscriptionOptionSelected()
          ? this.querySelector("label[for='purchaseTypeSubscription']")
          : this.querySelector("label[for='purchaseTypeOneTime']");
        if (label) label.click();
        else console.error(">>> [StayAI] setupEventListeners (pageshow): purchase type label missing");
        if (this.currentVariant) this.updateSellingPlans();
      }
    });
  }
  // Get variant ID from the URL and update the variant
  updateVariantID = () => {
    const variants = this.product?.variants ?? [];
    if (!variants.length || !this.currentVariant) return;
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has("variant")) return;
    const variantId = Number(urlParams.get("variant"));
    if (variantId && variantId !== this.currentVariant.id) {
      const variant = variants.find((v) => v.id === variantId);
      if (variant) {
        this.currentVariant = variant;
        this.updateSellingPlans();
      }
    }
  };
  // Monitor URL changes without overriding history methods
  observeUrlChanges = () => {
    let lastUrl = window.location.href;
    new MutationObserver(() => {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        this.updateVariantID();
      }
    }).observe(document, { subtree: true, childList: true });
  };
  // Populate the subscription frequency selector with selling plans for the current variant
  updateSellingPlans() {
    const stayWidget = this.querySelector(`.stayai-subscriptions`);
    if (!stayWidget) {
      console.error(">>> [StayAI] updateSellingPlans: container missing");
      return;
    }
    // Filter out bundle-type selling plan groups and map them by ID
    const groupMap = new Map(
      this.sellingPlanGroups.filter((g) => !g.options[0].name.toLowerCase().includes("bundle")).map((g) => [g.id, g]),
    );
    const sellingPlans = this.currentVariant.selling_plan_allocations
      .map((spa) => groupMap.get(spa.selling_plan_group_id)?.selling_plans.find((sp) => sp.id === spa.selling_plan_id))
      .filter(Boolean);
    if (sellingPlans.length === 0) {
      this.updateSellingPlanInput("");
      stayWidget.classList.toggle("is-visible", false);
      console.log(">>> [StayAI] updateSellingPlans: no selling plans");
      return;
    } else stayWidget.classList.toggle("is-visible", true);
    // Clear existing options and populate dropdown
    this.sellingPlanSelector.innerHTML = sellingPlans
      .map((plan) => `<option value="${plan.id}">${plan.options[0].value}</option>`)
      .join("");

    if (!this.isSubscriptionOptionSelected()) {
      this.addSelectFrequencyOption();
      this.updateSellingPlanInput("");
    } else {
      this.updateSellingPlanInput(sellingPlans[0]?.id || "");
    }
    // Show or hide the subscription frequency selector
    this.subscriptionBox.classList.toggle("is-visible", this.isSubscriptionOptionSelected());
    if (this.subscriptionBoxContainer)
      this.subscriptionBoxContainer.classList.toggle("subscriptionBoxContainer", this.isSubscriptionOptionSelected());
    // Update prices
    this.updateProductPrice(
      this.isSubscriptionOptionSelected() ? this.sellingPlanSelector.value : sellingPlans[0]?.id || "",
    );
  }
  // Add "Select frequency" option to the frequency selector
  addSelectFrequencyOption() {
    if (!this.sellingPlanSelector.querySelector('option[value=""]')) {
      this.sellingPlanSelector.insertAdjacentHTML("afterbegin", '<option value="" selected>Select frequency</option>');
    }
  }
  // Show the subscription frequency selector
  showSubscriptionBox() {
    this.subscriptionBox.classList.toggle("stayai-disable", false);
    this.subscriptionBox.classList.toggle("is-visible", true);
    if (this.subscriptionBoxContainer) this.subscriptionBoxContainer.classList.toggle("subscriptionBoxContainer", true);
    this.purchaseTypeSubscription.classList.toggle("is-selected", true);
    this.purchaseTypeOneTime.classList.toggle("is-selected", false);
    this.sellingPlanSelector.querySelector('option[value=""]')?.remove();
    this.updateSellingPlanInput(this.sellingPlanSelector.value);
  }
  // Hide the subscription frequency selector
  hideSubscriptionBox() {
    this.addSelectFrequencyOption();
    if (this.subscriptionBoxContainer) this.subscriptionBoxContainer.classList.toggle("is-visible", false);
    this.subscriptionBox.classList.toggle("is-visible", false);
    this.subscriptionBox.classList.toggle("disable", true);
    this.purchaseTypeSubscription.classList.toggle("is-selected", false);
    this.purchaseTypeOneTime.classList.toggle("is-selected", true);
    this.updateSellingPlanInput("");
  }
  // Check if the subscription option is selected
  isSubscriptionOptionSelected() {
    return this.purchaseTypeSubscription.classList.contains("is-selected");
  }
  // Save selling plan to localStorage (per product)
  saveSellingPlanToStorage(sellingPlanId) {
    if (sellingPlanId) {
      const storageKey = `stayai_selling_plan_${this.product.id}`;
      try {
        localStorage.setItem(storageKey, sellingPlanId);
      } catch (e) {
        console.warn(">>> [StayAI] saveSellingPlanToStorage:", e);
      }
    }
  }
  // Get saved selling plan from localStorage (per product)
  getSavedSellingPlan() {
    const storageKey = `stayai_selling_plan_${this.product.id}`;
    try {
      return localStorage.getItem(storageKey);
    } catch (e) {
      console.warn(">>> [StayAI] getSavedSellingPlan:", e);
      return null;
    }
  }
  // Clear saved selling plan (optional - for cleanup)
  clearSavedSellingPlan() {
    const storageKey = `stayai_selling_plan_${this.product.id}`;
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.warn(">>> [StayAI] clearSavedSellingPlan:", e);
    }
  }
  // Get the product form element using fallback selectors
  getProductForm() {
    let productForm = document.querySelector(
      'form[action="/cart/add"][data-type="add-to-cart-form"]:not(.installment), ' +
        'form[action="/cart/add"][id*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="shopify-product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"]:not(.installment)',
    );
    if (this.productFormId !== "") productForm = document.querySelector(this.productFormId) || productForm;
    return productForm;
  }
  //Add selling plan input to form with specified selling plan ID
  updateSellingPlanInput(sellingPlanId) {
    const productForm = this.getProductForm();
    if (!productForm) return;

    // Find or create the hidden input for selling plan
    let sellingPlanInput = productForm.querySelector("input[name='selling_plan']");
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement("input");
      sellingPlanInput.type = "hidden";
      sellingPlanInput.name = "selling_plan";
      sellingPlanInput.value = sellingPlanId;
      productForm.appendChild(sellingPlanInput);
    } else {
      sellingPlanInput.value = sellingPlanId;
    }

    // Save to localStorage whenever form input changes
    this.saveSellingPlanToStorage(sellingPlanId);

    // Dispatch event with the selling plan value
    this.fireStayAiEvent("stay-ai-subscription-changed", sellingPlanId);
  }
  updateProductPrice(sellingPlanId) {
    // Integration hook: Bypass internal price updates when external pricing is active
    if (this._useExternalPricing) {
      return;
    }

    const oneTimeLblPriceEl = this.querySelector("[data-stayai-onetime-price]");
    const subscriptionLblPriceEl = this.querySelector("[data-stayai-subscription-price]");
    const savingLblEl = this.querySelector(".subscription-savings");
    const onetimePrice = this.formatMoney(this.currentVariant.price);
    const compareAtPrice = this.formatMoney(this.currentVariant.compare_at_price);

    // Update one-time price
    this.updateOneTimePrice(oneTimeLblPriceEl, onetimePrice, compareAtPrice, 1);

    // Update subscription price from selling plan allocation
    this.updateSubscriptionPriceFromAllocation(sellingPlanId, subscriptionLblPriceEl, savingLblEl, onetimePrice);
  }
  // Update one-time price label with optional compare at price
  updateOneTimePrice(oneTimeLblPriceEl, onetimePrice, compareAtPrice, qty = 1) {
    if (oneTimeLblPriceEl && oneTimeLblPriceEl.textContent !== onetimePrice) {
      oneTimeLblPriceEl.innerHTML =
        compareAtPrice !== "$0.00" && compareAtPrice !== onetimePrice
          ? `<s>${compareAtPrice}</s> ${onetimePrice}`
          : onetimePrice;
    }
  }
  // Update subscription price label
  updateSubscriptionPrice(subscriptionLblPriceEl, subscriptionPrice, onetimePrice, qty = 1) {
    if (subscriptionLblPriceEl && subscriptionPrice !== subscriptionLblPriceEl.textContent) {
      subscriptionLblPriceEl.innerHTML =
        subscriptionPrice !== onetimePrice
          ? `<s class="fullPrice">${onetimePrice}</s>${subscriptionPrice}`
          : subscriptionPrice;
    }
  }
  // Update subscription price and savings from selling plan allocation
  updateSubscriptionPriceFromAllocation(sellingPlanId, subscriptionLblPriceEl, savingLblEl, onetimePrice) {
    const selectedSellingPlanAllocation = this.currentVariant.selling_plan_allocations.find(
      (spa) => spa.selling_plan_id == sellingPlanId,
    );
    if (!selectedSellingPlanAllocation) return;

    const sellingPlan = this.sellingPlanGroups
      .find((group) => group.selling_plans.some((sp) => sp.id == sellingPlanId))
      ?.selling_plans.find((sp) => sp.id == sellingPlanId);

    const subscriptionPrice = this.formatMoney(selectedSellingPlanAllocation.price);
    this.updateSubscriptionPrice(subscriptionLblPriceEl, subscriptionPrice, onetimePrice, 1);

    // Calculate and update savings label
    if (savingLblEl) {
      let discount = this.currentVariant.price - selectedSellingPlanAllocation.price;
      if (discount > 0) {
        if (sellingPlan?.price_adjustments[0].value_type === "percentage") {
          discount = `${sellingPlan.price_adjustments[0].value}%`;
        } else {
          discount = this.formatMoney(discount);
        }
        if (savingLblEl.textContent !== discount) savingLblEl.textContent = discount;
      } else {
        savingLblEl.textContent = "";
      }
    }
  }
  // Get specific selling plan by ID from groups filtered by app_id
  // Extracting selling plan data from the product JSON to access the discount percentage.
  getSellingPlanById(sellingPlanId, appId = StayAISubscriptions.STAY_APP_ID) {
    if (!this.sellingPlanGroups || !Array.isArray(this.sellingPlanGroups)) {
      return null;
    }
    if (!sellingPlanId) {
      return null;
    }
    // Filter groups by app_id (defaults to Stay AI app ID)
    const filteredGroups = this.sellingPlanGroups.filter((group) => group && group.app_id === appId);

    const sellingPlanIdNum = Number(sellingPlanId);
    if (isNaN(sellingPlanIdNum)) {
      return null;
    }
    for (const group of filteredGroups) {
      if (group && group.selling_plans && Array.isArray(group.selling_plans)) {
        const sellingPlan = group.selling_plans.find((sp) => sp && Number(sp.id) === sellingPlanIdNum);
        if (sellingPlan) {
          return sellingPlan;
        }
      }
    }
    return null;
  }
  // Get discount percentage from a selling plan ID
  getSellingPlanDiscountPercentage(sellingPlanId) {
    if (!sellingPlanId) {
      return null;
    }
    const sellingPlan = this.getSellingPlanById(sellingPlanId);
    if (!sellingPlan || !sellingPlan.price_adjustments || !Array.isArray(sellingPlan.price_adjustments) || sellingPlan.price_adjustments.length === 0) {
      return null;
    }
    const priceAdjustment = sellingPlan.price_adjustments[0];
    if (!priceAdjustment || priceAdjustment.value_type !== "percentage") {
      return null;
    }
    const value = Number(priceAdjustment.value);
    return Number.isFinite(value) ? value : null;
  }



  // Integration interface: Enable external pricing mode (used by integrations like Kaching)
  setUseExternalPricing(useExternal) {
    this._useExternalPricing = useExternal;
  }
  // Integration interface: Update prices from external source (e.g., Kaching)
  updatePricesFromExternal(pricingData) {
    const { discountedPrice, discountedPricePerItem, fullPrice, fullPricePerItem, vairantIdQuantiy, kachingDiscountPercentageRounded } = pricingData || {};

    const oneTimeLblPriceEl = this.querySelector("[data-stayai-onetime-price]");
    const subscriptionLblPriceEl = this.querySelector("[data-stayai-subscription-price]");
    const savingLblEl = this.querySelector(".subscription-savings");

    // Update one-time price
    this.updateOneTimePrice(oneTimeLblPriceEl, discountedPrice?.formatted, fullPrice?.formatted, vairantIdQuantiy?.quantity ?? 1);

    /**
     * Update subscription price (use prices from Kaching event)
     * Get the currently selected selling plan ID from the dropdown selector. If the one-time purchase option is selected and no selling plan ID is chosen, use the last saved selling plan ID from storage to continue the subscription price calculation, so the user can always see preview prices for each pack selection.
     */
    const currentSellingPlanId = this.sellingPlanSelector?.value || this.getSavedSellingPlan() || null;
    const sellingPlanDiscountPercentage = this.getSellingPlanDiscountPercentage(currentSellingPlanId);

    // Calculate onetimePrice once for use in both branches
    const onetimePrice = fullPrice?.formatted || this.formatMoney(this.currentVariant.price);
    const onetimePriceAmount = fullPrice?.amount || this.currentVariant.price;
    let subsSavingAmount = 0;

    // Mimic cart.js logic for discounted price calc: apply subs discount first, then Kaching.
    if (discountedPricePerItem?.amount && sellingPlanDiscountPercentage && sellingPlanDiscountPercentage > 0) {

      // #1 apply subs discount first
      const fullPricePerItem_amount = Number(fullPricePerItem?.amount);
      const fullPricePerItem_subsDiscountApplied =
        fullPricePerItem_amount * (1 - sellingPlanDiscountPercentage / 100);
      const fullPricePerItem_subsDiscountAppliedRounded = Number(fullPricePerItem_subsDiscountApplied).toFixed(0);

      // #2 cart property: original_line_price
      const quantity = vairantIdQuantiy?.quantity ?? 1;
      const originalLinePrice = fullPricePerItem_subsDiscountAppliedRounded * quantity;

      // #3 apply kaching discount
      const kachingDiscountAmount = Number(kachingDiscountPercentageRounded * originalLinePrice).toFixed(0);

      // Shopify (“each” per-unit rounding)
      const k_perUnitDiscount = kachingDiscountPercentageRounded * fullPricePerItem_subsDiscountAppliedRounded
      const k_perUnitDiscountFloored = Math.floor(k_perUnitDiscount);
      const k_ttlDiscount = k_perUnitDiscountFloored * quantity;

      console.log(">>> [StayAI] ttlDiscount:", { fullPricePerItem_subsDiscountAppliedRounded, k_perUnitDiscount, k_perUnitDiscountFloored,  k_ttlDiscount});

      const finalLinePrice_kachingDiscountApplied = Number(originalLinePrice - k_ttlDiscount).toFixed(0);

      // console.log(">>> [StayAI] discountedoneitem:", {  sellingPlanDiscountPercentage, fullPricePerItem_subsDiscountApplied, fullPricePerItem_subsDiscountAppliedRounded});

      console.log(">>> [StayAI] originalLinePrice:", {  finalLinePrice_kachingDiscountApplied});

      const subscriptionPrice = this.formatMoney(finalLinePrice_kachingDiscountApplied);

      subsSavingAmount = onetimePriceAmount - finalLinePrice_kachingDiscountApplied;
      this.updateSubscriptionPrice(subscriptionLblPriceEl, subscriptionPrice, onetimePrice, quantity);
    } else {
      // Fallback: use current variant's allocation to update the subscription price
      this.updateSubscriptionPriceFromAllocation(currentSellingPlanId, subscriptionLblPriceEl, savingLblEl, onetimePrice);

      // Calculate savings from allocation if available
      if (currentSellingPlanId) {
        const selectedSellingPlanAllocation = this.currentVariant.selling_plan_allocations.find(
          (spa) => spa.selling_plan_id == currentSellingPlanId,
        );
        if (selectedSellingPlanAllocation) {
          subsSavingAmount = onetimePriceAmount - selectedSellingPlanAllocation.price;
        }
      }
    }

    // Calculate and update savings label
    if (savingLblEl && subsSavingAmount > 0) {
      const discountFormatted = this.formatMoney(subsSavingAmount);
      if (savingLblEl.textContent !== discountFormatted) {
        savingLblEl.textContent = discountFormatted;
      }
    } else if (savingLblEl) {
      savingLblEl.textContent = "";
    }
  }

  // Toggle checked state based on the currently checked radio button
  toggleRadioButtons() {
    this.radioOneTime.checked = !this.radioOneTime.checked;
    this.radioSubscription.checked = !this.radioSubscription.checked;
  }
  // Format money based on Shopify's active currency
  formatMoney(money) {
    const { Shopify, theme } = window;
    const currency = Shopify?.currency?.active || "USD";
    const locale = Shopify?.locale || "en";
    const hasShopifyFormatMoney = Shopify && typeof Shopify?.formatMoney === "function";
    const hasThemeUtilsFormatMoney = theme?.utils && typeof theme?.utils?.formatMoney === "function";
    const formattedMoney = (money / 100).toFixed(2);
    let formatted;
    const currencyFormatter = new Intl.NumberFormat(locale, {
      currency,
      style: "currency",
    });
    if (!hasShopifyFormatMoney && !hasThemeUtilsFormatMoney) {
      return currencyFormatter.format(Number(formattedMoney));
    }
    formatted = hasShopifyFormatMoney ? Shopify.formatMoney(money) : theme.utils.formatMoney(money);
    return formatted || currencyFormatter.format(Number(formattedMoney));
  }
  fireStayAiEvent(eventName, sellingPlanId) {
    const event = new CustomEvent(eventName, {
      detail: {
        sellingPlanId: sellingPlanId,
      },
    });
    window.dispatchEvent(event);
  }
}
// Define the custom element
customElements.define("purchase-options", StayAISubscriptions);